import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  TextField,
  Link,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface VenueResUrlViewProps {
  venue: {
    id: string;
    resUrl?: string;
  };        
  onVenueUpdate?: (updatedVenue: any) => void;
}

export const VenueResUrlView: React.FC<VenueResUrlViewProps> = ({ 
  venue, 
  onVenueUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedResUrl, setEditedResUrl] = useState(venue.resUrl || '');
  const [copySuccess, setCopySuccess] = useState(false);

  const handleSave = () => {
    if (onVenueUpdate) {
      onVenueUpdate({
        ...venue,
        resUrl: editedResUrl
      });
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedResUrl(venue.resUrl || '');
    setIsEditing(false);
  };

  const handleCopy = async () => {
    if (venue.resUrl) {
      try {
        await navigator.clipboard.writeText(venue.resUrl);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000); // Reset after 2 seconds
      } catch (err) {
        console.error('Failed to copy text: ', err);
      }
    }
  };

  // Function to truncate URL for display
  const truncateUrl = (url: string) => {
    if (!url) return '';
    if (url.length <= 20) return url;
    return url.substring(0, 18) + '...';
  };

  if (!venue.resUrl && !isEditing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No URL
        </Typography>
        <IconButton 
          size="small" 
          onClick={() => setIsEditing(true)}
          sx={{ ml: 1 }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box>
      {isEditing ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            value={editedResUrl}
            onChange={(e) => setEditedResUrl(e.target.value)}
            placeholder="Enter URL"
            fullWidth
          />
          <IconButton size="small" onClick={handleSave} color="primary">
            <CheckIcon />
          </IconButton>
          <IconButton size="small" onClick={handleCancel} color="error">
            <CloseIcon />
          </IconButton>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={copySuccess ? "Copied!" : "Copy URL"}>
            <IconButton 
              size="small" 
              onClick={handleCopy}
              color={copySuccess ? "success" : "default"}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={venue.resUrl || ''}>
            <Link 
              href={venue.resUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              underline="hover"
              sx={{ ml: 0.5 }}
            >
              {truncateUrl(venue.resUrl || '')}
            </Link>
          </Tooltip>
          <IconButton 
            size="small" 
            onClick={() => setIsEditing(true)}
            sx={{ ml: 1 }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};
